import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Code,
  CopyButton,
  Group,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconCheck,
  IconCopy,
  IconDeviceFloppy,
  IconPuzzle,
  IconTrash,
} from "@tabler/icons-react";
import { gql } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import { notifications } from "@mantine/notifications";
import { useCallback, useEffect, useState } from "react";

// ── GraphQL Queries ──

const MY_INSTALLATIONS_QUERY = gql`
  query myMarketplaceInstallationsForModules($cursor: String, $count: Float) {
    myMarketplaceInstallations(page: { after: $cursor, first: $count }) {
      nodes {
        id
        status
        marketplaceApp {
          id
          name
          icon
          manifest {
            frontendModules {
              name
              displayName
              description
              exportName
              version
              propsSchema {
                key
                type
                label
                description
                required
                defaultValue
                param
                options {
                  label
                  value
                }
              }
              events {
                name
                description
              }
            }
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

const MODULE_CONFIGS_QUERY = gql`
  query platformModuleConfigs($platformId: ID!) {
    platformModuleConfigs(platformId: $platformId) {
      id
      marketplaceAppId
      moduleName
      props {
        key
        value
      }
    }
  }
`;

const MODULE_CONFIG_SET_MUTATION = gql`
  mutation platformModuleConfigSet($input: PlatformModuleConfigSetInput!) {
    platformModuleConfigSet(input: $input) {
      id
      marketplaceAppId
      moduleName
      props {
        key
        value
      }
    }
  }
`;

const MODULE_CONFIG_DELETE_MUTATION = gql`
  mutation platformModuleConfigDelete(
    $input: PlatformModuleConfigDeleteInput!
  ) {
    platformModuleConfigDelete(input: $input)
  }
`;

// ── Types ──

interface FrontendModulePropOption {
  label: string;
  value: string;
}

interface FrontendModulePropSchema {
  type: "string" | "number" | "boolean" | "select" | "route-param";
  label: string;
  description?: string;
  required: boolean;
  defaultValue?: string | number | boolean;
  options?: FrontendModulePropOption[];
  param?: string;
}

interface FrontendModuleEvent {
  name: string;
  description?: string;
}

interface RawFrontendModulePropSchema extends FrontendModulePropSchema {
  key: string;
}

interface RawFrontendModule {
  name: string;
  displayName: string;
  description?: string;
  exportName: string;
  version: string;
  propsSchema: RawFrontendModulePropSchema[];
  events: FrontendModuleEvent[];
}

interface FrontendModule {
  name: string;
  displayName: string;
  description?: string;
  exportName: string;
  version: string;
  propsSchema: Record<string, FrontendModulePropSchema>;
  events: FrontendModuleEvent[];
}

interface InstalledApp {
  id: string;
  status: string;
  marketplaceApp: {
    id: string;
    name: string;
    icon?: string;
    manifest?: {
      frontendModules?: FrontendModule[];
    };
  };
}

const transformRawModule = (raw: RawFrontendModule): FrontendModule => ({
  ...raw,
  propsSchema: (raw.propsSchema ?? []).reduce(
    (acc, { key, ...schema }) => {
      acc[key] = schema;
      return acc;
    },
    {} as Record<string, FrontendModulePropSchema>,
  ),
});

interface SavedModuleConfig {
  id: string;
  marketplaceAppId: string;
  moduleName: string;
  props: Record<string, string | number | boolean>;
}

interface ModuleManagerProps {
  platformId: string;
  pageId?: string;
  routeParams?: string[];
}

// ── Prop Field Renderer ──

function PropField({
  propKey: _propKey,
  schema,
  value,
  onChange,
  routeParams,
}: {
  propKey: string;
  schema: FrontendModulePropSchema;
  value: string | number | boolean | undefined;
  onChange: (val: string | number | boolean) => void;
  routeParams: string[];
}) {
  const label = schema.label + (schema.required ? " *" : "");
  const description = schema.description;

  switch (schema.type) {
    case "string":
      return (
        <TextInput
          label={label}
          description={description}
          placeholder={`Enter ${schema.label.toLowerCase()}`}
          value={(value as string) ?? (schema.defaultValue as string) ?? ""}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      );

    case "number":
      return (
        <NumberInput
          label={label}
          description={description}
          placeholder={`Enter ${schema.label.toLowerCase()}`}
          value={(value as number) ?? (schema.defaultValue as number) ?? 0}
          onChange={(val) => onChange(val ?? 0)}
        />
      );

    case "boolean":
      return (
        <Checkbox
          label={label}
          description={description}
          checked={
            (value as boolean) ?? (schema.defaultValue as boolean) ?? false
          }
          onChange={(e) => onChange(e.currentTarget.checked)}
        />
      );

    case "select":
      return (
        <Select
          label={label}
          description={description}
          placeholder={`Select ${schema.label.toLowerCase()}`}
          data={
            schema.options?.map((opt) => ({
              label: opt.label,
              value: opt.value,
            })) ?? []
          }
          value={(value as string) ?? (schema.defaultValue as string) ?? null}
          onChange={(val) => onChange(val ?? "")}
        />
      );

    case "route-param":
      return (
        <Select
          label={label}
          description={
            description ??
            `Route parameter${schema.param ? ` (default: ${schema.param})` : ""}`
          }
          placeholder="Select a route parameter"
          data={routeParams.map((p) => ({ label: p, value: p }))}
          value={(value as string) ?? (schema.defaultValue as string) ?? null}
          onChange={(val) => onChange(val ?? "")}
        />
      );

    default:
      return (
        <TextInput
          label={label}
          description={`Unsupported type: ${schema.type}`}
          value={String(value ?? schema.defaultValue ?? "")}
          onChange={(e) => onChange(e.currentTarget.value)}
        />
      );
  }
}

// ── Module Card ──

function ModuleCard({
  appId,
  appName,
  module,
  routeParams,
  savedConfig,
  onSave,
  onDelete,
}: {
  appId: string;
  appName: string;
  module: FrontendModule;
  routeParams: string[];
  savedConfig?: SavedModuleConfig;
  onSave: (
    appId: string,
    moduleName: string,
    props: Record<string, string | number | boolean>,
  ) => Promise<void>;
  onDelete: (appId: string, moduleName: string) => Promise<void>;
}) {
  const propEntries = Object.entries(module.propsSchema ?? {});
  const hasProps = propEntries.length > 0;
  const hasEvents = module.events.length > 0;
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [propValues, setPropValues] = useState<
    Record<string, string | number | boolean>
  >(() => {
    // Initialize from saved config if available, otherwise use defaults
    const defaults: Record<string, string | number | boolean> = {};
    for (const [key, schema] of propEntries) {
      if (savedConfig?.props?.[key] !== undefined) {
        defaults[key] = savedConfig.props[key];
      } else if (schema.defaultValue !== undefined) {
        defaults[key] = schema.defaultValue;
      }
    }
    return defaults;
  });

  // Update when savedConfig changes externally
  useEffect(() => {
    if (!savedConfig) return;
    const currentPropEntries = Object.entries(module.propsSchema ?? {});
    setPropValues((prev) => {
      const updated = { ...prev };
      for (const [key] of currentPropEntries) {
        if (savedConfig.props?.[key] !== undefined) {
          updated[key] = savedConfig.props[key];
        }
      }
      return updated;
    });
  }, [savedConfig, module.propsSchema]);

  const handlePropChange = (key: string, val: string | number | boolean) => {
    setPropValues((prev) => ({ ...prev, [key]: val }));
  };

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave(appId, module.name, propValues);
    } finally {
      setSaving(false);
    }
  }, [appId, module.name, propValues, onSave]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    try {
      await onDelete(appId, module.name);
    } finally {
      setDeleting(false);
    }
  }, [appId, module.name, onDelete]);

  const snippet = `{% module app-id:"${appId}" module-name:"${module.name}" %}`;

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Group justify="space-between" mb="sm">
        <div>
          <Group gap="xs">
            <Text fw={600} size="md">
              {module.displayName}
            </Text>
            <Badge size="sm" variant="light">
              v{module.version}
            </Badge>
            {savedConfig && (
              <Badge size="sm" variant="filled" color="green">
                Configured
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            {appName}
          </Text>
          {module.description && (
            <Text size="sm" c="dimmed" mt={4}>
              {module.description}
            </Text>
          )}
        </div>
      </Group>

      {/* Template snippet */}
      <Text size="sm" fw={500} mb="xs">
        Add to Liquid template:
      </Text>
      <Group gap="xs" mb="md" wrap="nowrap">
        <Code block style={{ flex: 1, fontSize: 12 }}>
          {snippet}
        </Code>
        <CopyButton value={snippet}>
          {({ copied, copy }) => (
            <ActionIcon
              color={copied ? "teal" : "gray"}
              variant="subtle"
              onClick={copy}
              title={copied ? "Copied" : "Copy snippet"}
            >
              {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
            </ActionIcon>
          )}
        </CopyButton>
      </Group>

      {/* Props configuration form */}
      {hasProps && (
        <>
          <Text size="sm" fw={500} mb="xs">
            Configuration
          </Text>
          <Stack gap="sm" mb="md">
            {propEntries.map(([key, schema]) => (
              <PropField
                key={key}
                propKey={key}
                schema={schema}
                value={propValues[key]}
                onChange={(val) => handlePropChange(key, val)}
                routeParams={routeParams}
              />
            ))}
          </Stack>
          <Group gap="sm">
            <Button
              size="xs"
              leftSection={<IconDeviceFloppy size={14} />}
              loading={saving}
              onClick={handleSave}
            >
              Save Configuration
            </Button>
            {savedConfig && (
              <Button
                size="xs"
                variant="subtle"
                color="red"
                leftSection={<IconTrash size={14} />}
                loading={deleting}
                onClick={handleDelete}
              >
                Reset
              </Button>
            )}
          </Group>
        </>
      )}

      {/* Events */}
      {hasEvents && (
        <>
          <Text size="sm" fw={500} mb="xs" mt={hasProps ? "md" : undefined}>
            Events
          </Text>
          <Stack gap={4}>
            {module.events.map((evt) => (
              <Group key={evt.name} gap="xs">
                <Code>{evt.name}</Code>
                {evt.description && (
                  <Text size="xs" c="dimmed">
                    {evt.description}
                  </Text>
                )}
              </Group>
            ))}
          </Stack>
        </>
      )}
    </Card>
  );
}

// ── Main Component ──

export function ModuleManager({
  platformId,
  pageId: _pageId,
  routeParams = [],
}: ModuleManagerProps) {
  const { data, loading } = useQuery<any>(MY_INSTALLATIONS_QUERY, {
    variables: { count: 100 },
  });

  const { data: configsData, refetch: refetchConfigs } = useQuery<any>(
    MODULE_CONFIGS_QUERY,
    {
      variables: { platformId },
      skip: !platformId,
    },
  );

  const [setConfig] = useMutation<any>(MODULE_CONFIG_SET_MUTATION);
  const [deleteConfig] = useMutation<any>(MODULE_CONFIG_DELETE_MUTATION);

  const savedConfigs: SavedModuleConfig[] = (
    configsData?.platformModuleConfigs ?? []
  ).map(
    (c: {
      id: string;
      marketplaceAppId: string;
      moduleName: string;
      props: Array<{ key: string; value: string }>;
    }) => ({
      ...c,
      props: Object.fromEntries(c.props.map((p) => [p.key, p.value])),
    }),
  );

  const handleSave = useCallback(
    async (
      appId: string,
      moduleName: string,
      props: Record<string, string | number | boolean>,
    ) => {
      const propsArray = Object.entries(props).map(([key, value]) => ({
        key,
        value: String(value),
      }));
      await setConfig({
        variables: {
          input: {
            platformId,
            marketplaceAppId: appId,
            moduleName,
            props: propsArray,
          },
        },
      });
      await refetchConfigs();
      notifications.show({
        title: "Saved",
        message: `Configuration for ${moduleName} saved successfully`,
        color: "green",
        autoClose: 3000,
      });
    },
    [platformId, setConfig, refetchConfigs],
  );

  const handleDelete = useCallback(
    async (appId: string, moduleName: string) => {
      await deleteConfig({
        variables: {
          input: {
            platformId,
            marketplaceAppId: appId,
            moduleName,
          },
        },
      });
      await refetchConfigs();
      notifications.show({
        title: "Reset",
        message: `Configuration for ${moduleName} has been reset`,
        color: "blue",
        autoClose: 3000,
      });
    },
    [platformId, deleteConfig, refetchConfigs],
  );

  const installations: InstalledApp[] = (
    data?.myMarketplaceInstallations?.nodes ?? []
  ).map((node: any) => ({
    ...node,
    marketplaceApp: {
      ...node.marketplaceApp,
      manifest: node.marketplaceApp?.manifest
        ? {
            ...node.marketplaceApp.manifest,
            frontendModules: (
              node.marketplaceApp.manifest.frontendModules ?? []
            ).map(transformRawModule),
          }
        : undefined,
    },
  }));

  const appsWithModules = installations.filter((inst) => {
    const modules = inst.marketplaceApp?.manifest?.frontendModules;
    return modules && modules.length > 0 && inst.status === "ACTIVE";
  });

  if (loading) {
    return <Text c="dimmed">Loading modules...</Text>;
  }

  if (appsWithModules.length === 0) {
    return (
      <Alert
        icon={<IconPuzzle size={16} />}
        title="No modules available"
        color="blue"
      >
        Install marketplace apps with frontend modules to configure them here.
      </Alert>
    );
  }

  return (
    <Stack gap="lg">
      <Title order={4}>Module Configuration</Title>
      <Text size="sm" c="dimmed">
        Configure frontend modules from your installed marketplace apps. Copy
        the Liquid snippet to embed a module in your platform templates.
      </Text>
      {appsWithModules.map((inst) => {
        const modules = inst.marketplaceApp.manifest?.frontendModules ?? [];
        return modules.map((mod) => {
          const config = savedConfigs.find(
            (c) =>
              c.marketplaceAppId === inst.marketplaceApp.id &&
              c.moduleName === mod.name,
          );
          return (
            <ModuleCard
              key={`${inst.marketplaceApp.id}-${mod.name}`}
              appId={inst.marketplaceApp.id}
              appName={inst.marketplaceApp.name}
              module={mod}
              routeParams={routeParams}
              savedConfig={config}
              onSave={handleSave}
              onDelete={handleDelete}
            />
          );
        });
      })}
    </Stack>
  );
}
